import { mm, type Um } from './units.js';
import type { EdgeId, Issue, PanelPart, PanelRole } from './model.js';
import { EDGE_IDS } from './model.js';

export type EdgePolicyId = 'all-exposed' | 'visible-open-doors' | 'workshop-standard' | 'manual';
/**
 * band       — edge band applied
 * none       — no treatment
 * unresolved — needs a user decision (e.g. partial contact)
 * factory    — factory-finished edge (e.g. post-formed worktop) — no extra band
 * seal       — cut sealing against moisture (not an edge band, separate operation)
 */
export type EdgeDecisionKind = 'band' | 'none' | 'unresolved' | 'factory' | 'seal';

export type Environment = 'dry' | 'wet';

/** Project-level band choice and the unit's environment, passed into edge resolution. */
export interface EdgeContext {
  environment: Environment;
  bands: { standard: EdgeBandSpec; front: EdgeBandSpec };
}

export interface EdgeBandSpec {
  /** Catalog id of an EdgeBandProduct, or null if not chosen yet. */
  productId: string | null;
  thickness: Um;
  width: Um;
}

export interface EdgeDecision {
  edge: EdgeId;
  mode: 'auto' | 'manual';
  decision: EdgeDecisionKind;
  band: EdgeBandSpec | null;
  reason: string;
  ruleSource: string;
}

export interface EdgeOverride {
  decision: EdgeDecisionKind;
  band?: EdgeBandSpec | null;
  note?: string;
}

/** Overrides keyed by `${partLocalKey}:${edgeId}` inside a cabinet instance. */
export type EdgeOverrides = Record<string, EdgeOverride>;

export interface EdgePolicy {
  id: EdgePolicyId;
  label: string;
  defaultBand: EdgeBandSpec;
  frontBand: EdgeBandSpec;
  /** Band the back edge of carcass parts when not covered. */
  bandBackEdges: boolean;
  /** Band uncovered edges that face up/down/sideways. */
  bandAllUncovered: boolean;
  /** How partially covered edges are treated. */
  partial: 'unresolved' | 'band-whole';
  /** A fixed part this close (µm) in front of an edge counts as enclosing it (e.g. shelf clearance). */
  enclosedGap: Um;
  /** Band uncovered edges facing the floor (bottom ends of sides etc.). */
  bandDownwardEdges: boolean;
}

const BAND_08: EdgeBandSpec = { productId: null, thickness: mm(0.8), width: mm(22) };
const BAND_2: EdgeBandSpec = { productId: null, thickness: mm(2), width: mm(22) };

export const EDGE_POLICIES: Record<EdgePolicyId, EdgePolicy> = {
  'all-exposed': {
    id: 'all-exposed',
    label: 'Sve izložene ivice',
    defaultBand: BAND_08,
    frontBand: BAND_2,
    bandBackEdges: true,
    bandAllUncovered: true,
    partial: 'band-whole',
    enclosedGap: mm(2),
    bandDownwardEdges: true,
  },
  'visible-open-doors': {
    id: 'visible-open-doors',
    label: 'Vidljive ivice uz otvorena vrata',
    defaultBand: BAND_08,
    frontBand: BAND_2,
    bandBackEdges: false,
    bandAllUncovered: false,
    partial: 'unresolved',
    enclosedGap: mm(2),
    bandDownwardEdges: false,
  },
  'workshop-standard': {
    id: 'workshop-standard',
    label: 'Standard radionice',
    defaultBand: BAND_08,
    frontBand: BAND_2,
    bandBackEdges: false,
    bandAllUncovered: true,
    partial: 'unresolved',
    enclosedGap: mm(2),
    bandDownwardEdges: false,
  },
  manual: {
    id: 'manual',
    label: 'Ručno',
    defaultBand: BAND_08,
    frontBand: BAND_2,
    bandBackEdges: false,
    bandAllUncovered: false,
    partial: 'unresolved',
    enclosedGap: mm(2),
    bandDownwardEdges: false,
  },
};

const NEVER_BANDED: PanelRole[] = ['back', 'drawer-bottom'];
const FRONT_ROLES: PanelRole[] = ['door', 'drawer-front'];

/**
 * Auto edge decision for one edge. Coverage by movable parts (doors, drawers) is ignored:
 * an edge hidden by a door is NOT considered protected.
 */
export function autoEdgeDecision(part: PanelPart, edge: EdgeId, all: Map<string, PanelPart>, policy: EdgePolicy, ctx?: EdgeContext): EdgeDecision {
  const d = autoDryDecision(part, edge, all, ctx ? { ...policy, defaultBand: ctx.bands.standard, frontBand: ctx.bands.front } : policy);
  // Wet areas: an exposed cut that gets no band must be sealed. Joints closed by fixed parts and hidden
  // drawer-box edges stay as they are; an edge merely hidden by a door is NOT considered protected.
  if (ctx?.environment === 'wet' && policy.id !== 'manual' && d.decision === 'none' && !d.ruleSource.endsWith('/contact') && !d.ruleSource.endsWith('role:drawer-box')) {
    return { ...d, decision: 'seal', band: null, reason: `Mokri čvor: izloženi rez se zaptiva (nije kant). ${d.reason}`, ruleSource: `${d.ruleSource}+wet` };
  }
  return d;
}

function autoDryDecision(part: PanelPart, edge: EdgeId, all: Map<string, PanelPart>, policy: EdgePolicy): EdgeDecision {
  const e = part.edges[edge];
  const src = `policy:${policy.id}`;
  const mk = (decision: EdgeDecisionKind, band: EdgeBandSpec | null, reason: string, ruleSource = src): EdgeDecision => ({
    edge,
    mode: 'auto',
    decision,
    band,
    reason,
    ruleSource,
  });

  if (policy.id === 'manual') return mk('none', null, 'Ručni profil: bez automatskog kanta.');
  if (NEVER_BANDED.includes(part.role)) return mk('none', null, 'Leđa / dno ladice se ne kantiraju.', `${src}/role:${part.role}`);
  if (FRONT_ROLES.includes(part.role)) return mk('band', policy.frontBand, 'Fronta: sve četiri ivice.', `${src}/role:front`);

  const fixedContacts = e.contacts.filter((c) => {
    const o = all.get(c.partId);
    return !!o && !o.movable && c.gap <= policy.enclosedGap;
  });
  const coverageFromFixed = Math.min(1, fixedContacts.reduce((s, c) => s + c.fraction, 0));

  if (part.role === 'drawer-side' || part.role === 'drawer-back') {
    if (e.facing === 'top') return mk('band', policy.defaultBand, 'Gornja ivica kutije ladice.', `${src}/role:drawer-box`);
    return mk('none', null, 'Skrivena ivica kutije ladice.', `${src}/role:drawer-box`);
  }

  if (coverageFromFixed >= 0.999) {
    return mk('none', null, `Ivica potpuno zatvorena spojem (${fixedContacts.map((c) => all.get(c.partId)!.label).join(', ')}).`, `${src}/contact`);
  }
  if (coverageFromFixed > 0) {
    if (policy.partial === 'band-whole') return mk('band', policy.defaultBand, 'Djelimično pokrivena ivica — kantira se cijela.', `${src}/partial`);
    return mk('unresolved', null, `Djelimično pokrivena ivica (${Math.round(coverageFromFixed * 100)}%) — potrebna odluka ili segmentacija.`, `${src}/partial`);
  }
  if (e.facing === 'front') return mk('band', policy.defaultBand, 'Prednja izložena ivica.', `${src}/front`);
  if (e.facing === 'back') {
    return policy.bandBackEdges
      ? mk('band', policy.defaultBand, 'Zadnja izložena ivica (profil kantira zadnje ivice).', `${src}/back`)
      : mk('none', null, 'Zadnja ivica — profil ne kantira zadnje ivice.', `${src}/back`);
  }
  if (e.facing === 'bottom' && !policy.bandDownwardEdges) {
    return mk('none', null, 'Ivica okrenuta prema podu — profil je ne kantira.', `${src}/downward`);
  }
  return policy.bandAllUncovered
    ? mk('band', policy.defaultBand, 'Nepokrivena izložena ivica.', `${src}/exposed`)
    : mk('none', null, 'Profil kantira samo prednje ivice.', `${src}/exposed`);
}

export interface PartEdges {
  partId: string;
  decisions: Record<EdgeId, EdgeDecision>;
}

export function resolveEdges(
  parts: PanelPart[],
  policy: EdgePolicy,
  overrides: EdgeOverrides,
  cabinetId: string,
  ctx?: EdgeContext,
): { edges: PartEdges[]; issues: Issue[] } {
  const defaultBand = ctx?.bands.standard ?? policy.defaultBand;
  const all = new Map(parts.map((p) => [p.id, p]));
  const issues: Issue[] = [];
  const known = new Set<string>();
  const edges: PartEdges[] = [];
  for (const part of parts) {
    if (!part.inCutList) continue;
    const decisions = {} as Record<EdgeId, EdgeDecision>;
    for (const edge of EDGE_IDS) {
      const key = `${part.localKey}:${edge}`;
      known.add(key);
      const ov = overrides[key];
      if (ov) {
        decisions[edge] = {
          edge,
          mode: 'manual',
          decision: ov.decision,
          band: ov.decision === 'band' ? (ov.band ?? defaultBand) : null,
          reason: ov.note ?? 'Ručna izmjena.',
          ruleSource: 'manual',
        };
      } else {
        decisions[edge] = autoEdgeDecision(part, edge, all, policy, ctx);
      }
      if (decisions[edge].decision === 'unresolved') {
        issues.push({ severity: 'warning', code: 'edge.unresolved', message: `${part.label} ${edge}: ${decisions[edge].reason}`, cabinetId, partId: part.id, edge });
      }
      const band = decisions[edge].band;
      if (decisions[edge].decision === 'band' && band && band.width < part.thickness + mm(2)) {
        issues.push({
          severity: 'error',
          code: 'edge.band-too-narrow',
          message: `${part.label} ${edge}: traka ${band.width / 1000} mm je preuska za ploču ${part.thickness / 1000} mm.`,
          cabinetId,
          partId: part.id,
        });
      }
    }
    edges.push({ partId: part.id, decisions });
  }
  for (const key of Object.keys(overrides)) {
    if (!known.has(key)) {
      issues.push({
        severity: 'warning',
        code: 'edge.override-orphaned',
        message: `Ručna izmjena ivice ${key} više nema odgovarajuću ivicu (promijenjena topologija).`,
        cabinetId,
        ref: key,
      });
    }
  }
  return { edges, issues };
}
